import React from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldArray, Form, Formik } from 'formik';

import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { ReactComponent as LinkedinIcon } from '../../../assets/images/linkedin-icon.svg';
import { ReactComponent as WebIcon } from '../../../assets/images/web-icon.svg';
import { ReactComponent as CrossIcon } from '../../../assets/images/x.svg';
import { FileUpload } from '../../../components/FileUpload/FileUpload';
import { FormikMultiSelectInput } from '../../../components/FormikMultiSelectInput/FormikMultiSelectInput';
import { FormikSelectInput } from '../../../components/FormikSelectInput/FormikSelectInput';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import { OpeningSelect } from '../../../components/OpeningSelect/OpeningSelect';
import { OrganizationSelect } from '../../../components/OrganizationSelect/OrganizationSelect';
import { ReferralFlowStepper } from '../../../components/ReferralFlowStepper/ReferralFlowStepper';
import { ReferralProgressBar } from '../../../components/ReferralProgressBar/ReferralProgressBar';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { CandidateInfoSection } from '../../../components/shared/CandidateInfoSection/CandidateInfoSection';
import { FormikUrlInput } from '../../../components/shared/FormikUrlInput/FormikUrlInput';
import Header from '../../../components/shared/Header/Header';
import LabelWithIcon from '../../../components/shared/Label/LabelWithIcon';
import { YourInfoSection } from '../../../components/shared/YourInfoSection/YourInfoSection';
import { useReferralFlowContext } from '../../../contexts/ReferralFlowContext';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  useGetReferralQuery,
  useGetSkillsQuery,
  useUpdateReferralAttentionStatusMutation,
  useUpdateReferralMutation,
} from '../../../redux/apis/referralApi';
import urls from '../../../routing/urls';

import styles from './Strengthen.module.scss';

// Note: Labels are translated in the component using useTranslation
export const OPEN_TO_NEW_OPPORTUNITIES_VALUES = ['Not open', 'Unsure', 'Passively Looking', 'Actively Looking'];

const calculateApplicationStrength = (values, strengthenQuestions) => {
  let answered = 0;
  let total = 0;

  const check = (enabled, hasValue) => {
    if (enabled) {
      total += 1;
      if (hasValue) answered += 1;
    }
  };

  check(
    strengthenQuestions.how_good_are_they,
    (values.selectedSkills?.length || 0) + (values.existingSkills?.length || 0) > 0
  );
  check(strengthenQuestions.know_the_person_level, !!values.know_the_person_level);
  check(strengthenQuestions.worked_with_person, !!values.worked_with_person);
  check(strengthenQuestions.open_for_new_opportunities_status, !!values.open_for_opportunities_status);
  // Diversity hire: the checkbox defaults to 'false' (unchecked) — only count as answered when actively ticked.
  check(strengthenQuestions.diversity_hire, values.diversity_hire === 'true' || values.diversity_hire === true);
  check(
    strengthenQuestions.attach_url,
    Array.isArray(values.number_of_links) &&
      values.number_of_links.length > 0 &&
      values.number_of_links[0].type === 'ln' &&
      values.number_of_links[0].value !== 'www.linkedin.com/in/' &&
      values.number_of_links[0].value !== ''
  );
  check(
    strengthenQuestions.attach_file,
    Array.isArray(values.license_available) && values.license_available.length > 0
  );

  // Baseline strength: every referral/application starts at 17%. The remaining
  // 83% is earned by answering enabled questions and added on top of the base.
  const BASE_STRENGTH = 17;

  if (total === 0) {
    return BASE_STRENGTH;
  }

  return Math.min(100, BASE_STRENGTH + Math.round((answered / total) * (100 - BASE_STRENGTH)));
};

// Mapping function to convert referRequest and skills to initialValues
const mapReferRequestToInitialValues = (referRequest, skillsList, linkedinUrl, t) => {
  let number_of_links = [
    { label: t('strengthen.webLinks.linkedin'), type: 'ln', value: linkedinUrl || 'www.linkedin.com/in/' },
    { label: t('strengthen.webLinks.website'), type: 'pw', value: '' },
  ];
  try {
    const links = JSON.parse(referRequest?.request_link_url_json || '[]');
    if (Array.isArray(links) && links.length > 0) {
      number_of_links = links.map((link, idx) => ({
        label: idx === 0 ? t('strengthen.webLinks.linkedin') : t('strengthen.webLinks.website'),
        type: link.type || (idx === 0 ? 'ln' : 'pw'),
        value: link.value || '',
      }));
      if (number_of_links.length < 2) {
        number_of_links.push({ label: t('strengthen.webLinks.website'), type: 'pw', value: '' });
      }
    }
  } catch (e) {
    /* ignore JSON parse errors */
  }

  const selectedSkills = (referRequest?.skills || [])
    .map((skill) => (skillsList || []).find((s) => s.name === skill)?.id || '')
    .filter((s) => s);
  const existingSkills =
    (referRequest?.skills || []).filter((skill) => !(skillsList || []).find((s) => s.name === skill)) || [];

  const license_available = referRequest?.request_pdf_files.map((file) => ({ ...file, is_selected: true })) || [];

  return {
    know_the_person_level: referRequest?.know_the_person_level?.toString() || '',
    worked_with_person: referRequest?.worked_with_person?.toString() || '',
    open_for_opportunities_status: referRequest?.open_for_opportunities_status || '',
    diversity_hire: referRequest?.diversity_hire ? 'true' : 'false',
    license_available,
    number_of_links,
    updateReferral: false,
    disableSkipButton: false,
    current_file_ids: license_available.map((f) => f.id),
    selectedSkills,
    existingSkills,
    skill_ids: referRequest.skills_object?.map((s) => s.id),
    skills_params: selectedSkills.map((s) => s.value).join(','),
  };
};

export const Strengthen = () => {
  const { t } = useTranslation('referral');
  const { companyName } = useParams();
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const { referralId } = useParams();
  const { data: referral, isFetching: isReferralFetching } = useGetReferralQuery({ referralId });
  const { setCurrentStep, strengthenQuestions } = useReferralFlowContext();
  // Progress bar is only meaningful when the master toggle is on AND at least one
  // individual question is enabled in Admin Web. Otherwise, hide it entirely.
  const hasAnyReferralQuestionEnabled =
    !!strengthenQuestions.enable_referral_questions &&
    !!(
      strengthenQuestions.how_good_are_they ||
      strengthenQuestions.know_the_person_level ||
      strengthenQuestions.worked_with_person ||
      strengthenQuestions.open_for_new_opportunities_status ||
      strengthenQuestions.diversity_hire ||
      strengthenQuestions.attach_url ||
      strengthenQuestions.attach_file
    );
  const referRequest = referral?.payload?.refer_request;
  const [updateReferral, { isLoading }] = useUpdateReferralMutation();
  const [updateReferralAttentionStatus] = useUpdateReferralAttentionStatusMutation();
  const { data: skills } = useGetSkillsQuery({ organizationId: referRequest?.organization_id });

  const isMobile = IsMobile();
  const personalInfo = {
    firstName: referRequest?.from_name?.split(' ')[0] || '',
    lastName: referRequest?.from_name?.split(' ')[1] || '',
    email: referRequest?.from_email || '',
    avatar_thumb: referRequest?.from_avatar_thumb || '',
  };
  const candidateInfo = {
    first_name: referRequest?.name?.split(' ')[0] || '',
    last_name: referRequest?.name?.split(' ')[1] || '',
    email: referRequest?.email || '',
    phone_number: referRequest?.phone_number?.full_number || '',
    linkedin_url: referRequest?.linkedin_link || '',
  };

  const customQuestions = referRequest?.custom_questions || [];
  // Use useMemo to compute initialValues based on referRequest and skills
  const initialValues = React.useMemo(
    () =>
      referRequest && skills?.payload?.skills
        ? mapReferRequestToInitialValues(referRequest, skills.payload.skills, candidateInfo?.linkedin_url, t)
        : {
            know_the_person_level: '',
            worked_with_person: '',
            open_for_opportunities_status: '',
            diversity_hire: '',
            license_available: [],
            number_of_links: [
              {
                label: t('strengthen.webLinks.linkedin'),
                type: 'ln',
                value: candidateInfo?.linkedin_url || 'www.linkedin.com/in/',
              },
              { label: t('strengthen.webLinks.website'), type: 'pw', value: '' },
            ],
            updateReferral: false,
            disableSkipButton: false,
            current_file_ids: [],
            selectedSkills: [],
            skills_params: '',
          },
    [referRequest, skills, t]
  );

  const handleSubmit = (values) => {
    const { license_available, selectedSkills, number_of_links, ...rest } = values;

    // Normalize selectedSkills to ensure they are IDs only
    const normalizedSelectedSkills = (selectedSkills || []).map((skill) =>
      typeof skill === 'object' ? skill.id : skill
    );

    const skills_params = [...normalizedSelectedSkills, ...(values.skill_ids || [])].map((s) => s).join(',');
    const referStrengthData = {
      ...rest,
      skills_params,
      number_of_links: number_of_links.filter(
        (link) => link.value && !(link.type === 'ln' && link.value === 'www.linkedin.com/in/')
      ),
    };

    const formData = new FormData();

    (license_available || []).forEach((file) => {
      if (file && !file.id && file.is_selected) {
        formData.append('pdf_files[]', file);
      }
    });

    formData.append('refer_strength_data', JSON.stringify(referStrengthData));

    updateReferral({ referralId: referRequest?.id, referralData: formData })
      .unwrap()
      .then(() => {
        updateReferralAttentionStatus({ referralId: referRequest?.id, attention_required: true });
        const url = companyName ? urls.companyReferralSuccess : urls.referralFlowSuccess;

        navigate(url.replace(':referralId', referralId).replace(':companyName', companyName));
      });
  };

  const getOpportunityLabelKey = (value) => {
    if (value === 'Not open') {
      return 'notOpen';
    }
    if (value === 'Unsure') {
      return 'unsure';
    }
    if (value === 'Passively Looking') {
      return 'passivelyLooking';
    }
    return 'activelyLooking';
  };

  const OPEN_TO_NEW_OPPORTUNITIES = OPEN_TO_NEW_OPPORTUNITIES_VALUES.map((value) => ({
    label: t(`strengthen.${getOpportunityLabelKey(value)}`),
    value,
  }));

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('headerTitle')}
          backButton={isAuthenticated && !companyName ? t('common:back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.container}>
          <ReferralFlowStepper currentStep={5} />

          <div className={styles.formSection}>
            <div className={styles.formHeader}>
              <h2>{t('sendAReferral')}</h2>
            </div>

            <div className={styles.previousInfo}>
              <YourInfoSection personalInfo={personalInfo} setCurrentStep={setCurrentStep} isEditable={false} />
              <CandidateInfoSection
                candidateInfo={candidateInfo}
                setCurrentStep={setCurrentStep}
                isEditable={false}
                isLoading={isReferralFetching}
                customQuestions={customQuestions}
              />
              {!isAuthenticated && !companyName && (
                <div className="space-y-[6px]">
                  <label htmlFor="company" className={styles.formLabel}>
                    {t('company')}
                  </label>
                  <OrganizationSelect value={referRequest?.organization_id} isDisabled />
                </div>
              )}
              <div className="space-y-[6px]">
                <label htmlFor="opening" className={styles.formLabel}>
                  {t('openingInfo')}
                </label>
                <OpeningSelect organizationId={referRequest?.organization_id} value={referRequest?.job_id} isDisabled />
              </div>
            </div>

            <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
              {({ values, setFieldValue }) => {
                const applicationStrength = calculateApplicationStrength(values, strengthenQuestions);

                return (
                  <Form>
                    {hasAnyReferralQuestionEnabled && (
                      <div className={styles.form}>
                        {strengthenQuestions.how_good_are_they && (
                          <FormikMultiSelectInput
                            className="strengthen-skills-select"
                            name="selectedSkills"
                            label={t('strengthen.whatGoodAt')}
                            options={(skills?.payload?.skills || []).map((skill) => ({
                              label: skill.name,
                              value: skill.id,
                              id: skill.id,
                              name: skill.name,
                            }))}
                            isMulti
                            getOptionValue={(option) => option.id}
                            getOptionLabel={(option) => option.name}
                            controlShouldRenderValue={false}
                            isClearable={false}
                            placeholder={t('strengthen.selectSkills')}
                          />
                        )}
                        <div className={styles.currentSkillsContainer}>
                          {values.existingSkills?.map((skill) => (
                            <LabelWithIcon
                              key={skill}
                              customStyles={styles.currentSkill}
                              tag={skill}
                              onDelete={() => {
                                setFieldValue(
                                  'existingSkills',
                                  values.existingSkills.filter((s) => s !== skill)
                                );
                              }}
                              deleteIcon={<CrossIcon />}
                            />
                          ))}
                          {values.selectedSkills.map((skillId) => {
                            const actualSkillId = typeof skillId === 'object' ? skillId.id : skillId;
                            const skill = (skills?.payload?.skills || []).find((s) => s.id === actualSkillId);
                            const skillName = skill?.name || (typeof skillId === 'object' ? skillId.name : skillId);
                            const skillKey = typeof skillId === 'object' ? skillId.id : skillId;

                            return (
                              <LabelWithIcon
                                key={skillKey}
                                customStyles={styles.currentSkill}
                                tag={skillName}
                                onDelete={() =>
                                  setFieldValue(
                                    'selectedSkills',
                                    values.selectedSkills.filter((s) => {
                                      const sId = typeof s === 'object' ? s.id : s;
                                      const skillIdToCompare = typeof skillId === 'object' ? skillId.id : skillId;
                                      return sId !== skillIdToCompare;
                                    })
                                  )
                                }
                                deleteIcon={<CrossIcon />}
                              />
                            );
                          })}
                        </div>

                        {strengthenQuestions.know_the_person_level && (
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="know_the_person_level">
                              {t('strengthen.howWellKnow')}
                            </label>
                            <div className={styles.radioGroup} data-testid="strengthen-know-person-level">
                              {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                                <label key={num} className={styles.radioLabel}>
                                  <input
                                    type="radio"
                                    name="know_the_person_level"
                                    value={num}
                                    checked={values.know_the_person_level === String(num)}
                                    onChange={(e) => setFieldValue('know_the_person_level', e.target.value)}
                                  />
                                  <p>{num}</p>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {strengthenQuestions.worked_with_person && (
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="worked_with_person">
                              {t('strengthen.workedDirectly')}
                            </label>
                            <div className={styles.workDirectlyRadioGroup} data-testid="strengthen-worked-with-person">
                              <label className={styles.workDirectlyRadioLabel}>
                                <input
                                  type="radio"
                                  name="worked_with_person"
                                  value="true"
                                  checked={values.worked_with_person === 'true'}
                                  onChange={(e) => setFieldValue('worked_with_person', e.target.value)}
                                  data-testid="strengthen-worked-yes"
                                />
                                {t('common:yes')}
                              </label>
                              <label className={styles.workDirectlyRadioLabel}>
                                <input
                                  type="radio"
                                  name="worked_with_person"
                                  value="false"
                                  checked={values.worked_with_person === 'false'}
                                  onChange={(e) => setFieldValue('worked_with_person', e.target.value)}
                                  data-testid="strengthen-worked-no"
                                />
                                {t('common:no')}
                              </label>
                            </div>
                          </div>
                        )}

                        {strengthenQuestions.open_for_new_opportunities_status && (
                          <FormikSelectInput
                            className="strengthen-opportunities-select"
                            name="open_for_opportunities_status"
                            label={t('strengthen.howOpen')}
                            placeholder={t('strengthen.select')}
                            options={OPEN_TO_NEW_OPPORTUNITIES}
                          />
                        )}

                        {strengthenQuestions.diversity_hire && (
                          <div className={styles.formGroup}>
                            <label className={styles.formLabel} htmlFor="diversity_hire">
                              {t('strengthen.diversityHire')}
                            </label>
                            <div className={styles.diversityCheckbox}>
                              <input
                                data-testid="strengthen-diversity-checkbox"
                                type="checkbox"
                                name="diversity_hire"
                                checked={values.diversity_hire === 'true'}
                                onChange={(e) => setFieldValue('diversity_hire', e.target.checked ? 'true' : 'false')}
                              />
                              <span>{t('common:yes')}</span>
                            </div>
                          </div>
                        )}

                        {strengthenQuestions.attach_file && !(referRequest?.request_pdf_files?.length > 0) && (
                          <FileUpload
                            fieldName="license_available"
                            files={values.license_available}
                            setFieldValue={setFieldValue}
                            data-testid="strengthen-file-upload"
                          />
                        )}

                        {strengthenQuestions.attach_url && (
                          <div className={styles.urlInputContainer}>
                            <label className={styles.additionalUrlsLabel} htmlFor="number_of_links">
                              {t('strengthen.links')}
                            </label>
                            {/* LinkedIn URL as a special link */}
                            <div className={styles.urlInputRow}>
                              <span className={styles.urlInputIcon}>
                                <LinkedinIcon className="w-[20px] h-[20px]" />
                              </span>
                              <FormikUrlInput
                                name="number_of_links.0.value"
                                label={t('strengthen.linkedinUrl')}
                                placeholder=""
                                shouldDisplayLabel={false}
                                isLinkedIn
                                disabled={!!candidateInfo?.linkedin_url}
                              />
                            </div>
                            <FieldArray name="number_of_links">
                              {({ push, remove }) => (
                                <div>
                                  {values.number_of_links.slice(1).map((link, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <div key={`additional-link-${index}`} className={styles.urlInputRow}>
                                      <span className={styles.urlInputIcon}>
                                        <WebIcon />
                                      </span>
                                      <FormikUrlInput
                                        name={`number_of_links.${index + 1}.value`}
                                        label={t('strengthen.additionalUrl')}
                                        placeholder=""
                                        shouldDisplayLabel={false}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => remove(index + 1)}
                                        className={styles.removeUrlButton}
                                      >
                                        &#x2715;
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      push({ label: t('strengthen.webLinks.website'), type: 'pw', value: '' })
                                    }
                                    className={styles.addMoreButton}
                                  >
                                    {t('strengthen.addMore')}
                                  </button>
                                </div>
                              )}
                            </FieldArray>
                          </div>
                        )}

                        <ReferralProgressBar
                          title={t('successPage.applicationStrength')}
                          applicationStrength={applicationStrength}
                        />
                      </div>
                    )}

                    <div className={styles.nextButtonContainer}>
                      {!values.disableSkipButton && (
                        <ButtonWithIcon
                          data-testid="strengthen-skip-button"
                          type="button"
                          variant="tertiary-36"
                          text={t('strengthen.skip')}
                          textClassName="text-[#314155]"
                          onClick={() => {
                            const url = companyName ? urls.companyReferralSuccess : urls.referralFlowSuccess;

                            navigate(`${url.replace(':referralId', referralId).replace(':companyName', companyName)}`);
                          }}
                        />
                      )}
                      <ButtonWithIcon
                        data-testid="strengthen-save-button"
                        type="submit"
                        variant="primary-36"
                        text={
                          <span className="flex items-center gap-[8px]">
                            {isLoading && <Spinner animation="border" size="sm" />} {t('common:save')}
                          </span>
                        }
                        textClassName="text-white"
                        disabled={isLoading}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};
